/**
 * Align blank paragraphs' left indent to the content they sit under.
 *
 * MEDirect templates (e.g. the Dr Sam Jones VIC IME template) style their
 * section headings with a left indent inherited from the `Heading1` style
 * (`<w:ind w:left="345"/>`), while the blank spacer lines around them are
 * `Normal` / `BodyText` at zero indent. In the docx-editor, a paragraph's
 * `indentLeft` renders as the block's `margin-left` — so it moves both the
 * text AND the caret. The caret-positioned dictation mic anchors to the
 * caret. The upshot: when the caret lands on one of those zero-indent blank
 * lines next to an indented heading, it (and the mic) render ~23px to the
 * LEFT of the heading text — "the selector is outside the heading indent".
 *
 * This pass walks the top-level paragraph flow and gives each EMPTY
 * paragraph the left indent of the nearest preceding non-empty paragraph,
 * so the caret / dictated text lines up under the heading it belongs to.
 *
 * Design notes:
 *   • Only `indentLeft` is synced. It's the attr the editor maps to
 *     `margin-left` (moving the block + caret). `indentFirstLine` maps to
 *     `text-indent`, which only shifts first-line glyphs and does nothing
 *     for an empty line's caret — leave it alone.
 *   • A non-paragraph block (table, etc.) resets the inheritance run, so
 *     blank spacers after a table don't inherit a stale heading indent.
 *   • Idempotent: a paragraph already at the inherited indent is left
 *     untouched, so a settled document yields zero edits — no dispatch,
 *     no autosave churn, no change-effect loop.
 *
 * @returns number of paragraphs whose indent was changed.
 */
import type { Node as PMNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";

const isParagraph = (node: PMNode): boolean =>
  node.type.name === "paragraph" || node.type.name === "doc_paragraph";

/** Effective left indent in twips; treats undefined/null as 0. */
const leftIndentOf = (node: PMNode): number => {
  const v = (node.attrs as { indentLeft?: number | null }).indentLeft;
  return v == null ? 0 : v;
};

export const alignBlankParagraphIndents = (view: EditorView): number => {
  const { state } = view;
  const { doc } = state;

  type Edit = { pos: number; node: PMNode; indentLeft: number | null };
  const edits: Edit[] = [];

  // null = no content paragraph seen yet in the current run (start of doc
  // or just after a non-paragraph block). A number is the effective left
  // indent (twips) of the most recent non-empty paragraph.
  let lastContentIndent: number | null = null;

  // Top-level flow only — paragraphs nested in tables have cell-relative
  // indentation and must not be touched. `offset` is the position directly
  // before the child, which is exactly what `setNodeMarkup` expects.
  doc.forEach((node, offset) => {
    if (!isParagraph(node)) {
      lastContentIndent = null;
      return;
    }
    if (node.textContent.trim().length > 0) {
      lastContentIndent = leftIndentOf(node);
      return;
    }
    // Empty paragraph: adopt the preceding content's indent when it differs.
    if (lastContentIndent === null) return; // nothing to inherit from yet
    const current = leftIndentOf(node);
    if (lastContentIndent !== current) {
      edits.push({
        pos: offset,
        node,
        // Store 0 back as null so we don't leave an explicit zero behind.
        indentLeft: lastContentIndent === 0 ? null : lastContentIndent,
      });
    }
  });

  if (edits.length === 0) return 0;

  let tr = state.tr;
  for (const e of edits) {
    tr = tr.setNodeMarkup(e.pos, undefined, {
      ...e.node.attrs,
      indentLeft: e.indentLeft,
    });
  }
  view.dispatch(tr);
  return edits.length;
};
